import { useEffect, useState } from "react"
import { client } from "@client/client"
import { getErrorMessage } from "@client/lib/errors"
import { toast } from "sonner"
import { useAdminSession, useIsSuperAdmin } from "./session-context"
import type { AdminRole } from "./session-context"
import { MembersIcon } from "./components/icons"
import { Select } from "./components/select"
import { Modal, ModalActions, ModalButton } from "./components/modal"
import { useConfirm } from "./components/confirm-modal"
import { RowActionsMenu } from "./components/row-actions-menu"
import { MembersPageSkeleton } from "./components/skeleton"

interface Member {
  id: string
  email: string
  name: string
  role: AdminRole
  avatarUrl: string | null
  invitedBy: string | null
  invitedAt: string
  lastLoginAt: string | null
  createdAt: string
}

export function MembersPage() {
  const isSuperAdmin = useIsSuperAdmin()
  const session = useAdminSession()
  const [members, setMembers] = useState<Member[]>([])
  const [roles, setRoles] = useState<AdminRole[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showInviteModal, setShowInviteModal] = useState(false)
  const [inviteForm, setInviteForm] = useState({ email: "", name: "", roleId: "" })
  const [inviting, setInviting] = useState(false)
  const [selectedMember, setSelectedMember] = useState<Member | null>(null)
  const [selectedRoleId, setSelectedRoleId] = useState("")
  const [updatingRole, setUpdatingRole] = useState(false)
  const { confirm, modal } = useConfirm()

  const fetchRoles = async () => {
    try {
      const res = await client.api.admin.roles.$get()
      const body = await res.json()
      if (res.ok && "data" in body) {
        setRoles(body.data)
        // Set default roleId if not set
        if (body.data.length > 0 && !inviteForm.roleId) {
          const defaultRole = body.data.find((r: AdminRole) => !r.isSuper) ?? body.data[0]
          if (defaultRole) {
            setInviteForm((prev) => ({ ...prev, roleId: defaultRole.id }))
          }
        }
      }
    } catch {
      console.error("Failed to fetch roles")
    }
  }

  const fetchMembers = async () => {
    try {
      const res = await client.api.admin.members.$get()
      const body = await res.json()
      if (!res.ok) {
        setError(getErrorMessage(body))
        return
      }
      if ("data" in body) {
        setMembers(body.data)
      }
    } catch {
      setError("Failed to load members")
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void fetchRoles()
    void fetchMembers()
  }, [])

  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!inviteForm.roleId) {
      toast.error("Please select a role")
      return
    }
    setInviting(true)
    try {
      const res = await client.api.admin.members.invite.$post({
        json: inviteForm,
      })
      const body = await res.json()
      if (!res.ok) {
        toast.error(getErrorMessage(body))
        return
      }
      toast.success("Member invited successfully")
      setShowInviteModal(false)
      setInviteForm({ email: "", name: "", roleId: inviteForm.roleId })
      void fetchMembers()
    } catch {
      toast.error("Failed to invite member")
    } finally {
      setInviting(false)
    }
  }

  const handleResendInvite = async (member: Member) => {
    try {
      const res = await client.api.admin.members[":id"]["resend-invite"].$post({
        param: { id: member.id },
      })
      const body = await res.json()
      if (!res.ok) {
        toast.error(getErrorMessage(body))
        return
      }
      toast.success(`Invitation resent to ${member.email}`)
      void fetchMembers()
    } catch {
      toast.error("Failed to resend invitation")
    }
  }

  const openRoleModal = (member: Member) => {
    setSelectedMember(member)
    setSelectedRoleId(member.role.id)
  }

  const closeRoleModal = () => {
    if (updatingRole) return
    setSelectedMember(null)
    setSelectedRoleId("")
  }

  const handleRoleChange = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!selectedMember || !selectedRoleId) return

    setUpdatingRole(true)
    try {
      const res = await client.api.admin.members[":id"].$patch({
        param: { id: selectedMember.id },
        json: { roleId: selectedRoleId },
      })
      const body = await res.json()
      if (!res.ok) {
        toast.error(getErrorMessage(body))
        return
      }
      toast.success("Role updated")
      setSelectedMember(null)
      setSelectedRoleId("")
      void fetchMembers()
    } catch {
      toast.error("Failed to update role")
    } finally {
      setUpdatingRole(false)
    }
  }

  const handleDelete = async (member: Member) => {
    const confirmed = await confirm({
      title: "Remove member",
      message: `Remove ${member.name} from the team? This cannot be undone.`,
      confirmLabel: "Remove member",
    })
    if (!confirmed) return
    try {
      const res = await client.api.admin.members[":id"].$delete({
        param: { id: member.id },
      })
      const body = await res.json()
      if (!res.ok) {
        toast.error(getErrorMessage(body))
        return
      }
      toast.success("Member removed")
      void fetchMembers()
    } catch {
      toast.error("Failed to remove member")
    }
  }

  if (!isSuperAdmin) {
    return (
      <div className="space-y-4">
        <h1 className="font-display text-2xl font-bold text-ink sm:text-3xl">Members</h1>
        <div className="rounded-[10px] border border-gold-500 bg-white p-8 text-center">
          <p className="text-ink/50">You don't have permission to manage members.</p>
        </div>
      </div>
    )
  }

  if (loading) {
    return <MembersPageSkeleton />
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="font-display text-2xl font-bold text-ink sm:text-3xl">Members</h1>
          <p className="mt-1 text-sm text-ink/60">Manage team members and their permissions.</p>
        </div>
        <button
          onClick={() => setShowInviteModal(true)}
          className="inline-flex h-10 items-center justify-center gap-2 rounded-full bg-forest-950 px-5 text-sm font-medium text-white transition-colors hover:bg-forest-900"
        >
          <MembersIcon className="h-4 w-4" />
          Invite Member
        </button>
      </div>

      {error ? (
        <div className="rounded-[10px] border border-red-200 bg-red-50 p-8 text-center">
          <p className="text-red-600">{error}</p>
        </div>
      ) : (
        <div className="overflow-hidden rounded-[10px] border border-gold-500 bg-white">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gold-500/50">
                  <th className="px-5 py-4 text-left text-xs font-semibold uppercase tracking-wider text-ink/60">
                    Member
                  </th>
                  <th className="px-5 py-4 text-left text-xs font-semibold uppercase tracking-wider text-ink/60">
                    Role
                  </th>
                  <th className="px-5 py-4 text-left text-xs font-semibold uppercase tracking-wider text-ink/60">
                    Status
                  </th>
                  <th className="px-5 py-4 text-right text-xs font-semibold uppercase tracking-wider text-ink/60">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gold-500/30">
                {members.map((member) => (
                  <tr key={member.id}>
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-3">
                        {member.avatarUrl ? (
                          <img
                            src={member.avatarUrl}
                            alt={member.name}
                            className="h-9 w-9 rounded-full object-cover"
                          />
                        ) : (
                          <span className="flex h-9 w-9 items-center justify-center rounded-full bg-forest-950 text-xs font-bold text-white">
                            {member.name
                              .split(" ")
                              .map((n) => n[0])
                              .join("")
                              .slice(0, 2)
                              .toUpperCase()}
                          </span>
                        )}
                        <div>
                          <p className="text-sm font-medium text-ink">{member.name}</p>
                          <p className="text-xs text-ink/50">{member.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-5 py-4">
                      <span className="inline-flex items-center rounded-full bg-forest-950/10 px-3 py-1 text-xs font-medium text-forest-950">
                        {member.role.name}
                      </span>
                    </td>
                    <td className="px-5 py-4">
                      <span
                        className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-medium ${
                          member.lastLoginAt
                            ? "bg-forest-950/10 text-forest-950"
                            : "bg-gold-500/20 text-ink"
                        }`}
                      >
                        {member.lastLoginAt ? "Active" : "Invited"}
                      </span>
                      <p className="mt-1 text-xs text-ink/50">
                        {member.lastLoginAt
                          ? `Last login ${new Date(member.lastLoginAt).toLocaleDateString()}`
                          : `Sent ${new Date(member.invitedAt).toLocaleDateString()}`}
                      </p>
                    </td>
                    <td className="px-5 py-4 text-right">
                      {member.id === session?.id ? (
                        <span className="text-xs text-ink/40">You</span>
                      ) : (
                        <RowActionsMenu
                          label={`Actions for ${member.name}`}
                          items={[
                            ...(member.lastLoginAt
                              ? []
                              : [{ label: "Resend invite", onClick: () => void handleResendInvite(member) }]),
                            { label: "Change role", onClick: () => openRoleModal(member) },
                            { label: "Remove member", onClick: () => void handleDelete(member) },
                          ]}
                        />
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {showInviteModal && (
        <Modal
          isOpen={showInviteModal}
          onClose={() => setShowInviteModal(false)}
          title="Invite member"
          subtitle="Send an invitation to join your admin team."
          maxWidth="md"
        >
          <form onSubmit={handleInvite} className="space-y-4">
              <label className="block">
                <span className="text-sm font-medium text-ink">Email</span>
                <input
                  type="email"
                  required
                  value={inviteForm.email}
                  onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
                  className="mt-1.5 block w-full rounded-lg border border-gold-500 bg-cream-50 px-4 py-2.5 text-sm text-ink placeholder:text-ink/40 focus:border-forest-700 focus:outline-none"
                  placeholder="name@example.com"
                />
              </label>
              <label className="block">
                <span className="text-sm font-medium text-ink">Name</span>
                <input
                  type="text"
                  required
                  value={inviteForm.name}
                  onChange={(e) => setInviteForm({ ...inviteForm, name: e.target.value })}
                  className="mt-1.5 block w-full rounded-lg border border-gold-500 bg-cream-50 px-4 py-2.5 text-sm text-ink placeholder:text-ink/40 focus:border-forest-700 focus:outline-none"
                  placeholder="Full name"
                />
              </label>
              <Select
                label="Role"
                value={roles.find(r => r.id === inviteForm.roleId)?.name || ""}
                onChange={(name) => {
                  const role = roles.find(r => r.name === name)
                  if (role) setInviteForm({ ...inviteForm, roleId: role.id })
                }}
                 options={roles.map((role) => role.name)}
                 placeholder="Select role..."
                 searchable
                 searchPlaceholder="Search roles..."
              />
            <ModalActions>
              <ModalButton onClick={() => setShowInviteModal(false)} disabled={inviting}>Cancel</ModalButton>
              <ModalButton type="submit" variant="primary" disabled={inviting}>
                {inviting ? "Inviting..." : "Send invite"}
              </ModalButton>
            </ModalActions>
          </form>
        </Modal>
      )}

      {selectedMember && (
        <Modal
          isOpen
          onClose={closeRoleModal}
          title="Change role"
          subtitle={`Update the role assigned to ${selectedMember.name}.`}
          maxWidth="sm"
        >
          <form onSubmit={handleRoleChange}>
            <Select
              label="Role"
              value={roles.find((role) => role.id === selectedRoleId)?.name || ""}
              onChange={(name) => {
                const role = roles.find((item) => item.name === name)
                if (role) setSelectedRoleId(role.id)
              }}
               options={roles.map((role) => role.name)}
               placeholder="Select role..."
               searchable
               searchPlaceholder="Search roles..."
            />
            <ModalActions>
              <ModalButton onClick={closeRoleModal} disabled={updatingRole}>Cancel</ModalButton>
              <ModalButton type="submit" variant="primary" disabled={updatingRole || !selectedRoleId}>
                {updatingRole ? "Saving..." : "Save role"}
              </ModalButton>
            </ModalActions>
          </form>
        </Modal>
      )}

      {modal}
    </div>
  )
}
